import { useMemo, useRef } from "react";
import ProductCard from "../../pages/products/ProductCard/ProductCard";
import { ProductWithoutIndex } from "../../interfaces/interfaces";

interface ProductCarouselProps {
  title?: string;
  products: ProductWithoutIndex[];
  loading?: boolean;
  viewAllUrl?: string;
}

const ProductCarousel = (props: ProductCarouselProps) => {
  const { title = "", products, loading = false, viewAllUrl } = props;
  const trackRef = useRef<HTMLDivElement | null>(null);

  const hasProducts = products && products.length > 0;

  const scrollByViewport = (direction: "prev" | "next") => {
    const track = trackRef.current;
    if (!track) return;

    const speed = 0.9;
    const scrollAmount = track.clientWidth * speed; // show slight next item
    const delta = direction === "next" ? scrollAmount : -scrollAmount;
    track.scrollBy({ left: delta, behavior: "smooth" });
  };

  const onPrev = () => scrollByViewport("prev");
  const onNext = () => scrollByViewport("next");

  const productList = useMemo(() => {
    if (!hasProducts) return null;
    return products.map((product, index) => (
      <div className="pc-item" key={`${product.id}-${index}`}>
        <ProductCard product={product} />
      </div>
    ));
  }, [hasProducts, products]);

  const skeletons = useMemo(() => {
    const count = 5;
    return Array.from({ length: count }).map((_, index) => (
      <div className="pc-item" key={`skeleton-${index}`}>
        <div className="pc-skeleton-card">
          <div className="pc-skeleton-image" />
          <div className="pc-skeleton-line" />
          <div className="pc-skeleton-line short" />
        </div>
      </div>
    ));
  }, []);

  return (
    <section
      className="product-carousel"
      aria-label={title || "Product carousel"}
      aria-busy={loading}
    >
      <div className="pc-header">
        {title && <h2>{title}</h2>}
        <div className="pc-header-actions">
          {viewAllUrl && (
            <a href={viewAllUrl} className="pc-view-all-btn">
              View All Products
            </a>
          )}
          <div className="pc-nav">
            <button className="pc-btn" aria-label="Previous" onClick={onPrev}>
              ‹
            </button>
            <button className="pc-btn" aria-label="Next" onClick={onNext}>
              ›
            </button>
          </div>
        </div>
      </div>

      <div ref={trackRef} className="pc-track" tabIndex={0}>
        {loading ? skeletons : productList}
      </div>
    </section>
  );
};

export default ProductCarousel;
